import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { useBarcode, useCore } from '../../hooks'
import { removeEmptyProperties } from '..'
import { FilterConfig, useFilters } from './useFilters'
import { useSettingsStore } from '../../contexts/SettingsContext'

// Store for preserving filter state across navigation
// This is a simple in-memory store - not persistent across page reloads
const filterStateStore = {
  processStatus: null as string | null,
  sort: null as string | null,
  filters: {} as Record<string, any>,
  searchParams: '' as string,
}

export function getStoredFilterSearch(): string {
  return filterStateStore.searchParams
}

/**
 * `useOrderFilters` - Custom hook for managing order filtering state and URL synchronization
 *
 * This hook provides a comprehensive solution for handling order filters with the following features:
 *
 * - URL as single source of truth when on Dashboard routes
 * - Preserves filter state for back navigation
 * - Provides debounced search functionality
 * - Handles filter reset and updates
 * - Constructs API-ready filter objects based on current selections
 * - Supports various filter types: carriers, checkouts, countries, depots, tags, etc.
 *
 * @returns {Object} An object containing:
 *   - Current filter state values
 *   - Filter update functions
 *   - Search handling functions
 *   - Reset function to clear all filters
 */

export function useOrderFilters() {
  const {
    core,
    searchType: coreSearchType,
    setSearchType: coreSetSearchType,
    searchString: coreSearchString,
    setSearchString: coreSetSearchString,
    childCompanyId: coreChildCompanyId,
    setSelectedChildCompany: coreSetSelectedChildCompany,
    selectedCarrierIds: coreSelectedCarrierIds,
    setSelectedCarrierIds: coreSetSelectedCarrierIds,
    selectedCheckoutId: coreSelectedCheckoutId,
    setSelectedCheckoutId: coreSetSelectedCheckoutId,
    selectedCountry: coreSelectedCountry,
    setSelectedCountry: coreSetSelectedCountry,
    selectedDepotId: coreSelectedDepotId,
    setSelectedDepotId: coreSetSelectedDepotId,
    selectedTagId: coreSelectedTagId,
    setSelectedTagId: coreSetSelectedTagId,
    selectedDate: coreSelectedDate,
    setSelectedDate: coreSetSelectedDate,
  } = useCore()

  const { strictDepotFilter: isStrictDepotFilter } = useSettingsStore()

  const history = useHistory()
  const { processStatus: paramProcessStatus }: any = useParams()

  // Persist processStatus across navigations, prioritizing URL parameter
  const effectiveProcessStatus = paramProcessStatus ?? filterStateStore.processStatus ?? 'all'
  useEffect(() => {
    if (paramProcessStatus) {
      filterStateStore.processStatus = paramProcessStatus
    }
  }, [paramProcessStatus])
  const [localSearchString, setLocalSearchString] = useState('')
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const filterConfig: FilterConfig = useMemo(
    () => ({
      searchType: {
        type: 'string',
        coreState: coreSearchType,
        coreSetter: coreSetSearchType,
        default: 'reservationNumber',
      },
      search: { type: 'string', coreState: coreSearchString, coreSetter: coreSetSearchString, default: '' },
      childCompanyId: {
        type: 'string',
        coreState: coreChildCompanyId,
        coreSetter: coreSetSelectedChildCompany,
        default: 'all',
      },
      carrierIds: {
        type: 'array',
        coreState: coreSelectedCarrierIds,
        coreSetter: coreSetSelectedCarrierIds,
        default: [],
      },
      checkoutId: {
        type: 'number',
        coreState: coreSelectedCheckoutId,
        coreSetter: coreSetSelectedCheckoutId,
        default: undefined,
      },
      countryId: { type: 'string', coreState: coreSelectedCountry, coreSetter: coreSetSelectedCountry, default: 'all' },
      depotId: {
        type: 'number',
        coreState: coreSelectedDepotId,
        coreSetter: coreSetSelectedDepotId,
        default: undefined,
      },
      tagIds: {
        type: 'array',
        coreState: coreSelectedTagId ? [coreSelectedTagId] : [],
        coreSetter: (ids: number[]) => coreSetSelectedTagId(ids?.[0]),
        default: [],
      },
      date: { type: 'date', coreState: coreSelectedDate, coreSetter: coreSetSelectedDate, default: undefined },
      groupId: { type: 'number', default: undefined },
      sort: { type: 'string', default: 'dateExpedition_asc' },
    }),
    [
      coreSearchType,
      coreSearchString,
      coreChildCompanyId,
      coreSelectedCarrierIds,
      coreSelectedCheckoutId,
      coreSelectedCountry,
      coreSelectedDepotId,
      coreSelectedTagId,
      coreSelectedDate,
      coreSetSearchType,
      coreSetSearchString,
      coreSetSelectedChildCompany,
      coreSetSelectedCarrierIds,
      coreSetSelectedCheckoutId,
      coreSetSelectedCountry,
      coreSetSelectedDepotId,
      coreSetSelectedTagId,
      coreSetSelectedDate,
    ],
  )

  const { values, setFilter, updateUrlParams, resetFilters, isFilterRoute } = useFilters(filterConfig, '/dashboard/')

  useBarcode(event => {
    if (event.type === 'BARCODE') {
      // Only handle barcode events on order-related pages
      const currentPath = window.location.pathname
      if (!currentPath.includes('/dashboard')) {
        console.log('not a dashboard path', currentPath)
        return
      }
      console.log('event useOrderF', event.data, /^(box|pr)-.+/i.test(event.data || ''))
      if (/^(box|pr)-.+/i.test(event.data || '')) {
        setFilter('searchType', 'box')
      }
      setFilter('search', event.data)
    }
  })

  const {
    searchType,
    search: searchString,
    childCompanyId,
    carrierIds: selectedCarrierIds = [],
    checkoutId: selectedCheckoutId,
    countryId: selectedCountry,
    depotId: selectedDepotId,
    tagIds: selectedTagIds = [],
    date: selectedDate,
    groupId,
    sort: sortParam,
  } = values

  // Persist sort across navigations, prioritizing URL parameter
  const effectiveSortParam = sortParam ?? filterStateStore.sort
  useEffect(() => {
    if (sortParam) {
      filterStateStore.sort = sortParam
    } else if (effectiveSortParam && isFilterRoute) {
      // Restore sort from store when returning to filter route
      setFilter('sort', effectiveSortParam)
    }
  }, [sortParam, effectiveSortParam, isFilterRoute, setFilter])

  // Parse sort parameter from URL (format: field_direction, e.g., "dateExpedition_asc")
  const { sort, direction } = useMemo(() => {
    if (effectiveSortParam) {
      const [field, dir] = effectiveSortParam.split('_')
      if (field && dir) {
        return {
          sort: field,
          direction: dir.toUpperCase(),
        }
      }
    }
    // Default sorting: dateExpedition ASC
    return { sort: 'dateExpedition', direction: 'ASC' }
  }, [effectiveSortParam])

  const setSort = useCallback(
    (field: string, dir: 'ASC' | 'DESC') => {
      setFilter('sort', `${field}_${dir.toLowerCase()}`)
    },
    [setFilter],
  )

  // Auto-apply and reset date for delivered and packing process status
  // Only react to process status changes, not date changes, to allow manual date selection
  const prevProcessStatusRef = useRef<string | null>(null)
  useEffect(() => {
    const formatDate = (d: Date) => {
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const prevStatus = prevProcessStatusRef.current
    const currentStatus = effectiveProcessStatus

    // Handle initial mount or status change
    if (prevStatus === null || prevStatus !== currentStatus) {
      if (currentStatus === 'delivered') {
        // When switching to 'delivered' (or on mount), set today's date if no date is set
        if (!selectedDate) {
          const todayStr = formatDate(new Date())
          setFilter('date', todayStr)
        }
      } else if (currentStatus === 'dispatched') {
        // When switching to 'dispatched' (or on mount), set today's date if no date is set
        if (!selectedDate) {
          const todayStr = formatDate(new Date())
          setFilter('date', todayStr)
        }
      } else if (prevStatus === 'delivered' || prevStatus === 'dispatched') {
        // Clear date when transitioning FROM 'delivered' or 'packing' to another status
        // This allows manual date selection on other statuses
        if (selectedDate) {
          setFilter('date', undefined)
        }
      }
      prevProcessStatusRef.current = currentStatus
    }
    // We intentionally avoid including setFilter in deps array to prevent re-runs due to reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveProcessStatus])

  useEffect(() => {
    setLocalSearchString(searchString || '')
  }, [searchString])

  const setSearchType = (value: string) => setFilter('searchType', value)
  const setSelectedChildCompany = (value: string) => setFilter('childCompanyId', value === 'all' ? undefined : value)
  const setSelectedCarrierIds = (ids: string[]) => setFilter('carrierIds', ids)
  const setSelectedCheckoutId = (value: string | number | undefined) =>
    setFilter('checkoutId', value && value !== 'all' ? Number(value) : undefined)
  const setSelectedCountry = (value: string | undefined) => setFilter('countryId', value === 'all' ? undefined : value)
  const setSelectedDepotId = (value: string | number | undefined) =>
    setFilter('depotId', value && value !== 'all' ? Number(value) : undefined)
  const setSelectedTagIds = (ids: number[]) => setFilter('tagIds', ids)
  const setSelectedTagId = (id: number | undefined) => setFilter('tagIds', id ? [id] : [])
  const setSelectedDate = (value: string | undefined) => setFilter('date', value)
  const setSearchString = (value: string) => setFilter('search', value)

  const resetFiltersWithSort = useCallback(() => {
    filterStateStore.sort = null
    resetFilters()
  }, [resetFilters])

  const plaintextFilterParams = useMemo(() => {
    const PTFEnabled = (type: string) => searchString && searchString?.length >= 0 && searchType === type
    const PTFNumEnabled = (type: string) => searchString && !isNaN(Number(searchString)) && searchType === type
    const RFilters = removeEmptyProperties(
      {
        box: PTFEnabled('box') && searchString,
        reservationNumber: PTFNumEnabled('reservationNumber') && Number(searchString),
        billNumber: PTFNumEnabled('billNumber') && Number(searchString),
        package: PTFEnabled('package') && searchString,
        customerFulltext: PTFEnabled('customer') && searchString,
      },
      el => el !== false,
    )
    return Object.keys(RFilters).length > 1 ? { fulltext: searchString } : RFilters
  }, [searchString, searchType])

  const filters = useMemo(() => {
    return removeEmptyProperties({
      processStatus: !searchString
        ? effectiveProcessStatus === 'all'
          ? undefined
          : effectiveProcessStatus
        : undefined,
      // Received, picking, packing: only reservation orders (skip when searching)
      ...(!searchString && ['received', 'picking', 'packing'].includes(effectiveProcessStatus)
        ? { status: 'reservation' }
        : {}),
      carriers: selectedCarrierIds.length > 0 ? selectedCarrierIds : undefined,
      checkouts: selectedCheckoutId && selectedCheckoutId !== 'all' ? [Number(selectedCheckoutId)] : undefined,
      shippingCountries: selectedCountry && selectedCountry !== 'all' ? [selectedCountry] : undefined,
      companies: childCompanyId && childCompanyId !== 'all' ? [childCompanyId] : undefined,
      group: groupId ? Number(groupId) : undefined,
      tags: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      processStatusChange:
        selectedDate && !selectedDate.includes('T')
          ? (() => {
              const date = new Date(selectedDate)
              return !isNaN(date.getTime()) ? date.toISOString() : undefined
            })()
          : undefined,
      processStatusUpdated:
        selectedDate && selectedDate.includes('T')
          ? (() => {
              const parsed = new Date(selectedDate)
              if (isNaN(parsed.getTime())) return undefined
              const left = new Date(parsed)
              left.setHours(0, 0, 0, 0)
              const right = new Date(parsed)
              right.setHours(right.getHours() + 1, 0, 0, 0)
              return { left_date: left.toISOString(), right_date: right.toISOString() }
            })()
          : undefined,
      [isStrictDepotFilter ? 'onlyDepots' : 'depots']:
        selectedDepotId && selectedDepotId !== 'all' ? [Number(selectedDepotId)] : undefined,
      ...plaintextFilterParams,
    })
  }, [
    selectedCarrierIds,
    selectedCheckoutId,
    selectedCountry,
    selectedDepotId,
    selectedTagIds,
    selectedDate,
    childCompanyId,
    groupId,
    isStrictDepotFilter,
    effectiveProcessStatus,
    plaintextFilterParams,
    searchString,
  ])

  // Construct the full dashboard URL with current filters
  const fullDashboardUrl = useMemo(() => {
    const params = new URLSearchParams()

    if (searchType && searchType !== 'all') params.set('searchType', searchType)
    if (searchString) params.set('search', searchString)
    if (childCompanyId && childCompanyId !== 'all') params.set('childCompanyId', childCompanyId)
    if (selectedCarrierIds && selectedCarrierIds.length > 0) params.set('carrierIds', selectedCarrierIds.join(','))
    if (selectedCheckoutId && selectedCheckoutId !== 'all') params.set('checkoutId', String(selectedCheckoutId))
    if (selectedCountry && selectedCountry !== 'all') params.set('countryId', selectedCountry)
    if (selectedDepotId && selectedDepotId !== 'all') params.set('depotId', String(selectedDepotId))
    if (selectedTagIds && selectedTagIds.length > 0) params.set('tagIds', selectedTagIds.join(','))
    if (selectedDate) params.set('date', selectedDate)
    if (groupId) params.set('groupId', String(groupId))

    const queryString = params.toString()
    filterStateStore.searchParams = queryString ? `?${queryString}` : ''
    const baseUrl =
      effectiveProcessStatus && effectiveProcessStatus !== 'all'
        ? `/dashboard/${effectiveProcessStatus}`
        : '/dashboard/packing'
    return queryString ? `${baseUrl}?${queryString}` : baseUrl
  }, [
    searchType,
    searchString,
    childCompanyId,
    selectedCarrierIds,
    selectedCheckoutId,
    selectedCountry,
    selectedDepotId,
    selectedTagIds,
    selectedDate,
    groupId,
    effectiveProcessStatus,
  ])

  return {
    searchType,
    searchString,
    localSearchString,
    selectedChildCompanyId: childCompanyId,
    selectedCarrierIds,
    selectedCheckoutId,
    selectedCountry,
    selectedDepotId,
    selectedTagId: selectedTagIds?.[0],
    selectedTagIds,
    selectedDate,
    processStatus: effectiveProcessStatus,
    filters,
    plaintextFilterParams,
    groupId,
    fullDashboardUrl,
    sort,
    direction,
    setSort,
    setSearchType,
    setSearchString,
    setLocalSearchString,
    setSelectedChildCompany,
    setSelectedCarrierIds,
    setSelectedCheckoutId,
    setSelectedCountry,
    setSelectedDepotId,
    setSelectedTagId,
    setSelectedTagIds,
    setSelectedDate,
    resetFilters: resetFiltersWithSort,
  }
}
